import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useForm, Controller } from 'react-hook-form';
import { course_createForm_createNewCourse, course_createForm_createDescription, course_createForm_multilingualHelpText, course_createForm_title, course_createForm_titlePlaceholder, course_createForm_titlePlaceholder_ko, course_createForm_titlePlaceholder_ja, course_createForm_createCourse, course_createForm_errors_minLength, course_createForm_errors_maxLength, common_cancel } from '@paraglide/messages';
import { Plus, X } from 'lucide-react';
import { Button, Input } from '@edusense/shared-ui-kit';
const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
`;
const LanguageTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
`;
const LanguageTab = styled.button`
  padding: 0.5rem 1rem;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  color: ${(props)=>props.active ? '#2563eb' : '#6b7280'};
  border-bottom: 2px solid ${(props)=>props.active ? '#2563eb' : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: #2563eb;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
const LanguageIndicator = styled.span`
  font-size: 1.25rem;
`;
const RequiredMark = styled.span`
  color: #ef4444;
  margin-left: 0.25rem;
`;
const HelpText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;
const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;
const CourseCreateFormI18n = ({ onSubmit, isLoading = false })=>{
    const [activeLanguage, setActiveLanguage] = useState('en');
    const { control, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {
            titleEn: '',
            titleKo: '',
            titleJa: ''
        }
    });
    const languages = [
        {
            code: 'en',
            label: 'English',
            flag: '🇺🇸',
            required: false
        },
        {
            code: 'ko',
            label: '한국어',
            flag: '🇰🇷',
            required: false
        },
        {
            code: 'ja',
            label: '日本語',
            flag: '🇯🇵',
            required: false
        }
    ];
    const onFormSubmit = (data)=>{
        // 최소 하나의 제목이 있는지 확인
        const hasAnyTitle = data.titleEn || data.titleKo || data.titleJa;
        if (!hasAnyTitle) {
            // 에러 처리: 최소 하나의 제목은 필요
            return;
        }
        onSubmit(data);
    };
    return /*#__PURE__*/ _jsxs(FormContainer, {
        onSubmit: handleSubmit(onFormSubmit),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-2xl font-bold mb-2",
                        children: course_createForm_createNewCourse()
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-600",
                        children: course_createForm_createDescription()
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(LanguageTabs, {
                        children: languages.map((lang)=>/*#__PURE__*/ _jsxs(LanguageTab, {
                                active: activeLanguage === lang.code,
                                onClick: ()=>setActiveLanguage(lang.code),
                                disabled: isLoading,
                                type: "button",
                                children: [
                                    /*#__PURE__*/ _jsx(LanguageIndicator, {
                                        children: lang.flag
                                    }),
                                    lang.label,
                                    lang.required && /*#__PURE__*/ _jsx(RequiredMark, {
                                        children: "*"
                                    })
                                ]
                            }, lang.code))
                    }),
                    /*#__PURE__*/ _jsx(HelpText, {
                        children: course_createForm_multilingualHelpText()
                    })
                ]
            }),
            activeLanguage === 'en' && /*#__PURE__*/ _jsx(Controller, {
                name: "titleEn",
                control: control,
                rules: {
                    minLength: {
                        value: 3,
                        message: course_createForm_errors_minLength()
                    },
                    maxLength: {
                        value: 100,
                        message: course_createForm_errors_maxLength()
                    }
                },
                render: ({ field, fieldState })=>/*#__PURE__*/ _jsx(Input, {
                        ...field,
                        label: course_createForm_title() + ' (English)',
                        placeholder: course_createForm_titlePlaceholder(),
                        required: true,
                        disabled: isLoading,
                        error: fieldState.error?.message,
                        autoFocus: true
                    })
            }),
            activeLanguage === 'ko' && /*#__PURE__*/ _jsx(Controller, {
                name: "titleKo",
                control: control,
                rules: {
                    minLength: {
                        value: 3,
                        message: course_createForm_errors_minLength()
                    },
                    maxLength: {
                        value: 100,
                        message: course_createForm_errors_maxLength()
                    }
                },
                render: ({ field, fieldState })=>/*#__PURE__*/ _jsx(Input, {
                        ...field,
                        label: course_createForm_title() + ' (한국어)',
                        placeholder: course_createForm_titlePlaceholder_ko(),
                        disabled: isLoading,
                        error: fieldState.error?.message,
                        autoFocus: true
                    })
            }),
            activeLanguage === 'ja' && /*#__PURE__*/ _jsx(Controller, {
                name: "titleJa",
                control: control,
                rules: {
                    minLength: {
                        value: 3,
                        message: course_createForm_errors_minLength()
                    },
                    maxLength: {
                        value: 100,
                        message: course_createForm_errors_maxLength()
                    }
                },
                render: ({ field, fieldState })=>/*#__PURE__*/ _jsx(Input, {
                        ...field,
                        label: course_createForm_title() + ' (日本語)',
                        placeholder: course_createForm_titlePlaceholder_ja(),
                        disabled: isLoading,
                        error: fieldState.error?.message,
                        autoFocus: true
                    })
            }),
            /*#__PURE__*/ _jsxs(ButtonGroup, {
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        variant: "ghost",
                        disabled: isLoading,
                        onClick: ()=>window.history.back(),
                        icon: /*#__PURE__*/ _jsx(X, {
                            size: 16
                        }),
                        children: common_cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "submit",
                        variant: "primary",
                        disabled: isLoading,
                        isLoading: isLoading,
                        icon: !isLoading ? /*#__PURE__*/ _jsx(Plus, {
                            size: 16
                        }) : undefined,
                        children: course_createForm_createCourse()
                    })
                ]
            })
        ]
    });
};
export default CourseCreateFormI18n;
